/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import React, { Children, createElement, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { getColor, retrieveComponentStyles, DEFAULT_THEME, getLineHeight } from '@zendeskgarden/react-theming';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _taggedTemplateLiteral(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  return Object.freeze(Object.defineProperties(strings, {
    raw: {
      value: Object.freeze(raw)
    }
  }));
}

function _templateObject() {
  var data = _taggedTemplateLiteral(["\n  display: block;\n  position: absolute;\n  top: ", "px;\n  ", ": ", ";\n  transition: background-color 0.1s ease-in-out, color 0.25s ease-in-out;\n  border: none; /* [1] */\n  border-radius: 50%;\n  background-color: transparent; /* [1] */\n  cursor: pointer;\n  padding: 0;\n  width: ", "px;\n  height: ", "px;\n  overflow: hidden;\n  color: ", ";\n  font-size: 0; /* [1] */\n  user-select: none;\n\n  &:hover {\n    color: ", ";\n  }\n\n  &:focus {\n    outline: none;\n  }\n\n  &[data-garden-focus-visible] {\n    background-color: ", ";\n    color: ", ";\n\n    &::-moz-focus-inner {\n      border: 0; /* [2] */\n    }\n  }\n\n  ", ";\n"]);
  _templateObject = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID = 'notifications.close';
var StyledClose = styled.button.attrs({
  'data-garden-id': COMPONENT_ID,
  'data-garden-version': '8.32.0'
})(_templateObject(), function (props) {
  return props.theme.space.base;
}, function (props) {
  return props.theme.rtl ? 'left' : 'right';
}, function (props) {
  return "".concat(props.theme.space.base, "px");
}, function (props) {
  return props.theme.space.base * 7;
}, function (props) {
  return props.theme.space.base * 7;
}, function (props) {
  return props.hue ? getColor(props.hue, props.hue === 'warningHue' ? 700 : 600, props.theme) : getColor('neutralHue', 600, props.theme);
}, function (props) {
  return props.hue ? getColor(props.hue, 800, props.theme) : getColor('neutralHue', 800, props.theme);
}, function (props) {
  return props.hue ? getColor(props.hue, props.hue === 'warningHue' ? 700 : 600, props.theme, 0.15) : getColor('neutralHue', 600, props.theme, 0.15);
}, function (props) {
  return props.hue ? getColor(props.hue, 800, props.theme) : getColor('neutralHue', 800, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID, props);
});
StyledClose.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$1() {
  var data = _taggedTemplateLiteral(["\n  margin: ", "px 0 0;\n  ", ";\n"]);
  _templateObject$1 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$1 = 'notifications.paragraph';
var StyledParagraph = styled.p.attrs({
  'data-garden-id': COMPONENT_ID$1,
  'data-garden-version': '8.32.0'
})(_templateObject$1(), function (props) {
  return props.theme.space.base * 2;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$1, props);
});
StyledParagraph.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$2() {
  var data = _taggedTemplateLiteral(["\n  margin: 0; /* [1] */\n  color: ", ";\n  font-weight: ", ";\n  ", ";\n"]);
  _templateObject$2 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$2 = 'notifications.title';
var StyledTitle = styled.div.attrs({
  'data-garden-id': COMPONENT_ID$2,
  'data-garden-version': '8.32.0'
})(_templateObject$2(), function (props) {
  return props.theme.colors.foreground;
}, function (props) {
  return props.isRegular ? props.theme.fontWeights.regular : props.theme.fontWeights.semibold;
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$2, props);
});
StyledTitle.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$3() {
  var data = _taggedTemplateLiteral(["\n  position: relative;\n  border: ", ";\n  border-radius: ", ";\n  box-shadow: ", ";\n  padding: ", ";\n  line-height: ", ";\n  font-size: ", ";\n  direction: ", ";\n\n  ", ";\n"]);
  _templateObject$3 = function _templateObject() {
    return data;
  };
  return data;
}
var boxShadow = function boxShadow(props) {
  var theme = props.theme;
  var space = theme.space,
      shadows = theme.shadows;
  var offsetY = "".concat(space.base * 5, "px");
  var blurRadius = "".concat(space.base * 7, "px");
  var color = getColor('chromeHue', 600, theme, 0.15);
  return shadows.lg(offsetY, blurRadius, color);
};
var colorStyles = function colorStyles(props) {
  var backgroundColor;
  var borderColor;
  var foregroundColor;
  if (props.hue) {
    backgroundColor = getColor(props.hue, 100, props.theme);
    borderColor = getColor(props.hue, 300, props.theme);
    foregroundColor = getColor(props.hue, props.type === 'info' ? 600 : 700, props.theme);
  } else {
    backgroundColor = props.theme.colors.background;
    borderColor = getColor('neutralHue', 300, props.theme);
    foregroundColor = getColor('neutralHue', 800, props.theme);
  }
  return css(["border-color:", ";background-color:", ";color:", ";"], borderColor, backgroundColor, foregroundColor);
};
var padding = function padding(props) {
  var space = props.theme.space;
  var paddingVertical = "".concat(space.base * 5, "px");
  var paddingHorizontal = "".concat(space.base * 10, "px");
  return "".concat(paddingVertical, " ").concat(paddingHorizontal);
};
var StyledBase = styled.div(_templateObject$3(), function (props) {
  return props.theme.borders.sm;
}, function (props) {
  return props.theme.borderRadii.md;
}, function (props) {
  return props.isFloating && boxShadow;
}, padding, function (props) {
  return getLineHeight(props.theme.space.base * 5, props.theme.fontSizes.md);
}, function (props) {
  return props.theme.fontSizes.md;
}, function (props) {
  return props.theme.rtl && 'rtl';
}, colorStyles);
StyledBase.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$4() {
  var data = _taggedTemplateLiteral(["\n  ", "\n  ", ";\n"]);
  _templateObject$4 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$3 = 'notifications.alert';
var colorStyles$1 = function colorStyles(props) {
  return css(["", "{color:", ";}"], StyledTitle, props.hue && getColor(props.hue, 800, props.theme));
};
var StyledAlert = styled(StyledBase).attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$3,
    'data-garden-version': '8.32.0',
    role: props.role === undefined ? 'alert' : props.role
  };
})(_templateObject$4(), colorStyles$1, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$3, props);
});
StyledAlert.defaultProps = {
  theme: DEFAULT_THEME
};

var ARRAY_VALIDATION_TYPE = ['success', 'warning', 'error', 'info'];

function _templateObject$5() {
  var data = _taggedTemplateLiteral(["\n  ", "\n\n  ", ";\n"]);
  _templateObject$5 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$4 = 'notifications.notification';
var colorStyles$2 = function colorStyles(props) {
  var type = props.type,
      theme = props.theme;
  var colors = theme.colors;
  var successHue = colors.successHue,
      dangerHue = colors.dangerHue,
      warningHue = colors.warningHue,
      foreground = colors.foreground;
  var color;
  switch (type) {
    case 'success':
      color = getColor(successHue, 600, theme);
      break;
    case 'error':
      color = getColor(dangerHue, 600, theme);
      break;
    case 'warning':
      color = getColor(warningHue, 700, theme);
      break;
    case 'info':
      color = foreground;
      break;
    default:
      color = 'inherit';
      break;
  }
  return css(["", "{color:", ";}"], StyledTitle, color);
};
var StyledNotification = styled(StyledBase).attrs(function (props) {
  return {
    'data-garden-id': COMPONENT_ID$4,
    'data-garden-version': '8.32.0',
    role: props.role === undefined ? 'status' : props.role
  };
})(_templateObject$5(), colorStyles$2, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$4, props);
});
StyledNotification.propTypes = {
  type: PropTypes.oneOf(ARRAY_VALIDATION_TYPE)
};
StyledNotification.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$6() {
  var data = _taggedTemplateLiteral(["\n  background-color: ", ";\n  color: ", "\n    ", ";\n"]);
  _templateObject$6 = function _templateObject() {
    return data;
  };
  return data;
}
var COMPONENT_ID$5 = 'notifications.well';
var StyledWell = styled(StyledBase).attrs({
  'data-garden-id': COMPONENT_ID$5,
  'data-garden-version': '8.32.0'
})(_templateObject$6(), function (props) {
  return props.isRecessed && getColor('neutralHue', 100, props.theme);
}, function (props) {
  return getColor('neutralHue', 600, props.theme);
}, function (props) {
  return retrieveComponentStyles(COMPONENT_ID$5, props);
});
StyledWell.defaultProps = {
  theme: DEFAULT_THEME
};

function _templateObject$7() {
  var data = _taggedTemplateLiteral(["\n  position: absolute;\n  right: ", ";\n  left: ", ";\n  margin-top: ", "px;\n  color: ", ";\n"]);
  _templateObject$7 = function _templateObject() {
    return data;
  };
  return data;
}
var StyledIcon = styled(function (_ref) {
  var children = _ref.children,
      props = _objectWithoutProperties(_ref, ["children"]);
  return React.cloneElement(Children.only(children), props);
})(_templateObject$7(), function (props) {
  return props.theme.rtl && "".concat(props.theme.space.base * 4, "px");
}, function (props) {
  return !props.theme.rtl && "".concat(props.theme.space.base * 4, "px");
}, function (props) {
  return props.theme.space.base / 2;
}, function (props) {
  return props.hue && getColor(props.hue, props.hue === 'warningHue' ? 700 : 600, props.theme);
});
StyledIcon.defaultProps = {
  theme: DEFAULT_THEME
};

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var _ref = /*#__PURE__*/createElement("g", {
  fill: "none",
  stroke: "currentColor"
}, /*#__PURE__*/createElement("circle", {
  cx: 7.5,
  cy: 8.5,
  r: 7
}), /*#__PURE__*/createElement("path", {
  strokeLinecap: "round",
  d: "M7.5 4.5V9"
}));

var _ref2 = /*#__PURE__*/createElement("circle", {
  cx: 7.5,
  cy: 12,
  r: 1,
  fill: "currentColor"
});

function SvgAlertErrorStroke(props) {
  return /*#__PURE__*/createElement("svg", _extends$1({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref, _ref2);
}

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var _ref$1 = /*#__PURE__*/createElement("g", {
  fill: "none",
  stroke: "currentColor"
}, /*#__PURE__*/createElement("path", {
  strokeLinecap: "round",
  strokeLinejoin: "round",
  d: "M4 9l2.5 2.5 5-5"
}), /*#__PURE__*/createElement("circle", {
  cx: 7.5,
  cy: 8.5,
  r: 7
}));

function SvgCheckCircleStroke(props) {
  return /*#__PURE__*/createElement("svg", _extends$2({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$1);
}

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var _ref$2 = /*#__PURE__*/createElement("path", {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M.88 13.77L7.06 1.86c.19-.36.7-.36.89 0l6.18 11.91c.17.33-.07.73-.44.73H1.32c-.37 0-.61-.4-.44-.73zM7.5 6v3.5"
});

var _ref2$1 = /*#__PURE__*/createElement("circle", {
  cx: 7.5,
  cy: 12,
  r: 1,
  fill: "currentColor"
});

function SvgAlertWarningStroke(props) {
  return /*#__PURE__*/createElement("svg", _extends$3({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$2, _ref2$1);
}

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

var _ref$3 = /*#__PURE__*/createElement("g", {
  stroke: "currentColor"
}, /*#__PURE__*/createElement("circle", {
  cx: 7.5,
  cy: 8.5,
  r: 7,
  fill: "none"
}), /*#__PURE__*/createElement("path", {
  strokeLinecap: "round",
  d: "M7.5 12.5V8"
}));

var _ref2$2 = /*#__PURE__*/createElement("circle", {
  cx: 7.5,
  cy: 5,
  r: 1,
  fill: "currentColor"
});

function SvgInfoStroke(props) {
  return /*#__PURE__*/createElement("svg", _extends$4({
    xmlns: "http://www.w3.org/2000/svg",
    width: 16,
    height: 16,
    viewBox: "0 0 16 16",
    focusable: "false",
    role: "presentation"
  }, props), _ref$3, _ref2$2);
}

var validationIcons = {
  success: SvgCheckCircleStroke,
  error: SvgAlertErrorStroke,
  warning: SvgAlertWarningStroke,
  info: SvgInfoStroke
};
var validationHues = {
  success: 'successHue',
  error: 'dangerHue',
  warning: 'warningHue',
  info: 'neutralHue'
};

var NotificationsContext = createContext(undefined);
var useNotificationsContext = function useNotificationsContext() {
  return useContext(NotificationsContext);
};

var Alert = React.forwardRef(function (props, ref) {
  var hue = validationHues[props.type];
  var Icon = validationIcons[props.type];
  return React.createElement(NotificationsContext.Provider, {
    value: hue
  }, React.createElement(StyledAlert, _extends({
    ref: ref,
    hue: hue
  }, props), React.createElement(StyledIcon, {
    hue: hue
  }, React.createElement(Icon, null)), props.children));
});
Alert.displayName = 'Alert';
Alert.propTypes = {
  type: PropTypes.oneOf(ARRAY_VALIDATION_TYPE).isRequired
};

var Notification = React.forwardRef(function (props, ref) {
  var Icon = props.type ? validationIcons[props.type] : SvgInfoStroke;
  var hue = props.type && validationHues[props.type];
  return React.createElement(StyledNotification, _extends({
    ref: ref,
    type: props.type,
    isFloating: true
  }, props), props.type && React.createElement(StyledIcon, {
    hue: hue
  }, React.createElement(Icon, null)), props.children);
});
Notification.displayName = 'Notification';
Notification.propTypes = {
  type: PropTypes.oneOf(ARRAY_VALIDATION_TYPE)
};

var Well = React.forwardRef(function (props, ref) {
  return React.createElement(StyledWell, _extends({
    ref: ref
  }, props));
});
Well.displayName = 'Well';
Well.propTypes = {
  isRecessed: PropTypes.bool,
  isFloating: PropTypes.bool
};

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

var _ref$4 = /*#__PURE__*/createElement("path", {
  stroke: "currentColor",
  strokeLinecap: "round",
  d: "M3 9l6-6m0 6L3 3"
});

function SvgXStroke(props) {
  return /*#__PURE__*/createElement("svg", _extends$5({
    xmlns: "http://www.w3.org/2000/svg",
    width: 12,
    height: 12,
    viewBox: "0 0 12 12",
    focusable: "false",
    role: "presentation"
  }, props), _ref$4);
}

var Close = React.forwardRef(function (props, ref) {
  var hue = useNotificationsContext();
  return React.createElement(StyledClose, _extends({
    ref: ref,
    hue: hue
  }, props), React.createElement(SvgXStroke, null));
});
Close.displayName = 'Close';

var Paragraph = React.forwardRef(function (props, ref) {
  return React.createElement(StyledParagraph, _extends({
    ref: ref
  }, props));
});
Paragraph.displayName = 'Paragraph';

var Title = React.forwardRef(function (props, ref) {
  return React.createElement(StyledTitle, _extends({
    ref: ref
  }, props));
});
Title.displayName = 'Title';

export { Alert, Close, Notification, Paragraph, Title, Well };
